import { cn } from "@gymflow/helpers";
import React from "react";

import { useClubSettings } from "../../providers";
import { ShopCreditPackSelect } from "./ShopCreditPackSelect";
import ShopGiftVoucher from "./ShopGiftVoucher";
import { ShopMembershipSelect } from "./ShopMembershipSelect";
import { ShopProductSelect } from "./ShopProductSelect";
import { ShopCartType } from "./ShopTypes";

export type ShopItemPickerProps = {
  cart?: ShopCartType;
  setCart: (value: ShopCartType, stayInSection?: boolean) => void;
  hideShopSidebar: () => void;
  showShopSidebar: () => void;
  sidebarState:
    | ({
        isVisible: boolean;
        currentShopTab?: number;
      } & {
        onClose?: () => void;
      })
    | undefined;
  setSidebarState: (
    param:
      | {
          isVisible: boolean;
          currentShopTab?: number;
        }
      | undefined,
  ) => void;
  userHasActiveMembershipSubscription: boolean;
};

export const ShopItemPicker: React.FC<ShopItemPickerProps> = ({
  cart,
  setCart,
  hideShopSidebar,
  showShopSidebar,
  sidebarState,
  setSidebarState,
  userHasActiveMembershipSubscription,
}) => {
  const { featureFlags, shopSettings } = useClubSettings();

  const tabs = [
    {
      title: "Recurring",
      body: (
        <ShopMembershipSelect
          type="recurring"
          cart={cart}
          setCart={setCart}
          hideShopSidebar={hideShopSidebar}
          showShopSidebar={showShopSidebar}
          userHasActiveMembershipSubscription={
            userHasActiveMembershipSubscription
          }
        />
      ),
    },
    {
      title: "Pre-Paid",
      body: (
        <ShopMembershipSelect
          type="prepaid"
          cart={cart}
          setCart={setCart}
          hideShopSidebar={hideShopSidebar}
          showShopSidebar={showShopSidebar}
          userHasActiveMembershipSubscription={
            userHasActiveMembershipSubscription
          }
        />
      ),
    },
    {
      title: "Trials",
      body: (
        <ShopMembershipSelect
          type="trial"
          cart={cart}
          setCart={setCart}
          hideShopSidebar={hideShopSidebar}
          showShopSidebar={showShopSidebar}
          userHasActiveMembershipSubscription={
            userHasActiveMembershipSubscription
          }
        />
      ),
    },
    {
      title: "Credit Packs",
      body: (
        <ShopCreditPackSelect
          cart={cart}
          setCart={setCart}
          hideShopSidebar={hideShopSidebar}
          showShopSidebar={showShopSidebar}
        />
      ),
    },
    {
      title: "Products",
      body: (
        <ShopProductSelect
          cart={cart}
          setCart={setCart}
          hideShopSidebar={hideShopSidebar}
          showShopSidebar={showShopSidebar}
        />
      ),
    },
    ...(featureFlags?.FE_VOUCHER_CHECKOUT && shopSettings.voucherSalesEnabled
      ? [
          {
            title: "Gift Voucher",
            body: <ShopGiftVoucher setCart={setCart} />,
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-[45vh] w-full flex-col">
      <div className="flex h-14 w-full flex-row items-center overflow-x-auto whitespace-nowrap border-b border-gray-200 sm:justify-evenly sm:overflow-x-visible">
        {tabs.map((tab, idx) => (
          <div
            key={idx}
            className={cn(
              "hover:bg-primary-50 -mb-px sm:flex-1 inline-flex min-w-max cursor-pointer items-center justify-center whitespace-pre px-4 py-4 sm:px-0 text-sm font-semibold text-gray-500 transition-all duration-100 ease-in-out",
              {
                "border-secondary-500 text-secondary-500 -mb-[2px] border-b-2":
                  idx === (sidebarState?.currentShopTab ?? 0),
              },
            )}
            onClick={() => {
              setSidebarState({
                ...sidebarState,
                isVisible: sidebarState?.isVisible ?? false,
                currentShopTab: idx,
              });
            }}
          >
            {tab.title}
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {tabs[sidebarState?.currentShopTab ?? 0].body}
      </div>
    </div>
  );
};
